<main class="content">
  @if (summaryState$ | async; as state) {
    @if (state.kind === 'loading') {
      <output class="state" aria-live="polite">
        <mat-spinner
          diameter="40"
          [attr.aria-label]="'fmsDiagnose.shared.loading' | translate"
        ></mat-spinner>
      </output>
    } @else if (state.kind === 'error') {
      <div class="state" role="alert">
        <mat-icon aria-hidden="true">error_outline</mat-icon>
        <strong>{{ 'fmsDiagnose.shared.error' | translate }}</strong>
        <button mat-stroked-button type="button" (click)="requestRetry()">
          {{ 'fmsDiagnose.shared.retry' | translate }}
        </button>
      </div>
    } @else {
      <output
        class="match"
        [class.match--found]="hasMatch(state.data)"
        [class.match--missing]="!hasMatch(state.data)"
        aria-live="polite"
      >
        <mat-icon aria-hidden="true">{{ hasMatch(state.data) ? 'check' : 'warning' }}</mat-icon>
        <div>
          <h2>{{ matchTitle(state.data) }}</h2>
          <p>{{ matchDescription(state.data) }}</p>
        </div>
      </output>

      <section class="section" aria-labelledby="fms-diagnose-administration-title">
        <div class="section-heading">
          <div>
            <h2 id="fms-diagnose-administration-title">
              <mat-icon aria-hidden="true">business</mat-icon>
              {{ 'fmsDiagnose.tabs.linkage.administration' | translate }}
            </h2>
          </div>
        </div>

        <dl class="administration-details">
          <dt>{{ 'fmsDiagnose.tabs.linkage.customer' | translate }}</dt>
          <dd>{{ state.data.customer.name }}</dd>
          <dt>{{ 'fmsDiagnose.tabs.linkage.customerNumber' | translate }}</dt>
          <dd>{{ state.data.customer.customerNumber }}</dd>
          @if (state.data.lastSynchronizationCompletedOn) {
            <dt>{{ 'fmsDiagnose.tabs.linkage.lastSynchronizationCompleted' | translate }}</dt>
            <dd>{{ state.data.lastSynchronizationCompletedOn | date: 'medium' }}</dd>
          }
        </dl>

        @if (state.data.customer.locations.length > 0) {
          <div class="location-list">
            <h3 data-test="fms-diagnose-location-heading">
              <mat-icon aria-hidden="true">location_on</mat-icon>
              {{ locationLabel(state.data.customer.locations.length) }}
            </h3>
            @for (location of state.data.customer.locations; track location.shipToId) {
              <article class="location" data-test="fms-diagnose-location">
                <div>
                  <h4>{{ location.name }}</h4>
                  <p>{{ formatAddress(location.address) || unavailable() }}</p>
                </div>
                <div class="kvk-list">
                  <span class="field-label">
                    {{ kvkLabel(location.kvkNumbers.length) }}
                  </span>
                  <div>
                    @for (kvkNumber of location.kvkNumbers; track kvkNumber) {
                      <span class="value-chip" data-test="fms-diagnose-kvk-chip">
                        {{ kvkNumber }}
                      </span>
                    }
                  </div>
                </div>
              </article>
            }
          </div>
        }
      </section>

      <section
        class="section"
        aria-labelledby="fms-diagnose-links-title"
        data-test="fms-diagnose-provider-links"
      >
        <div class="section-heading">
          <div>
            <h2 id="fms-diagnose-links-title">
              <mat-icon aria-hidden="true">link</mat-icon>
              {{ 'fmsDiagnose.tabs.linkage.providerLinks.title' | translate }}
            </h2>
            <p class="section-description">
              {{ 'fmsDiagnose.tabs.linkage.providerLinks.subtitle' | translate }}
            </p>
          </div>
        </div>
        @if (state.data.providerLinks.length === 0) {
          <div class="empty-links" data-test="fms-diagnose-no-provider-links">
            <mat-icon aria-hidden="true">link_off</mat-icon>
            <span>{{ 'fmsDiagnose.tabs.linkage.noStoredLinks' | translate }}</span>
          </div>
        } @else {
          @if (groupProviderLinks(state.data.providerLinks); as providerGroups) {
            <div class="link-board" data-test="fms-diagnose-link-board">
              <div
                class="provider-lanes"
                [class.provider-lanes--single]="providerGroups.length === 1"
              >
                @for (providerGroup of providerGroups; track providerGroup.provider) {
                  <section
                    class="provider-lane"
                    data-test="fms-diagnose-provider-lane"
                    [attr.data-provider]="providerGroup.provider"
                  >
                    <header class="provider-lane-header">
                      <h3>{{ providerGroup.provider }}</h3>
                    </header>
                    <div class="kvk-rows">
                      @for (kvkGroup of providerGroup.kvkGroups; track kvkGroup.key) {
                        <article class="kvk-row" data-test="fms-diagnose-kvk-row">
                          <h4 class="kvk-identity">
                            <span class="field-label">
                              {{ kvkLabel(1) }}
                            </span>
                            <strong class="kvk-value" data-test="fms-diagnose-kvk-value">
                              {{
                                kvkGroup.kvkNumber ||
                                  ('fmsDiagnose.tabs.linkage.kvkUnavailable' | translate)
                              }}
                            </strong>
                          </h4>
                          <div class="external-ids">
                            <span class="field-label">
                              {{ externalNamesAndFarmIdsLabel(kvkGroup.links.length) }}
                            </span>
                            <ul>
                              @for (link of kvkGroup.links; track link.id) {
                                <li class="external-id-pill" data-test="fms-diagnose-external-id">
                                  <strong class="farm-name" data-test="fms-diagnose-farm-name">
                                    {{
                                      link.externalFarmName.trim() ||
                                        ('fmsDiagnose.tabs.linkage.farmNameUnavailable' | translate)
                                    }}
                                  </strong>
                                  <code>{{ link.externalFarmId || unavailable() }}</code>
                                </li>
                              }
                            </ul>
                          </div>
                        </article>
                      }
                    </div>
                  </section>
                }
              </div>
            </div>
          }
        }
      </section>
    }
  }

  <section class="search" aria-labelledby="fms-diagnose-search-title">
    <div class="section-heading">
      <div>
        <h2 id="fms-diagnose-search-title">
          <mat-icon aria-hidden="true">search</mat-icon>
          {{ 'fmsDiagnose.tabs.linkage.search.title' | translate }}
        </h2>
        <p>{{ 'fmsDiagnose.tabs.linkage.search.subtitle' | translate }}</p>
        <p>{{ 'fmsDiagnose.tabs.linkage.search.readOnlySubtitle' | translate }}</p>
      </div>
    </div>

    <form class="search-form" [formGroup]="searchForm">
      <mat-form-field appearance="outline">
        <mat-label>{{ 'provider' | translate }}</mat-label>
        <mat-select formControlName="provider" data-test="fms-diagnose-provider">
          <mat-option value="Dacom">Dacom</mat-option>
          <mat-option value="Agrovision"> Agrovision </mat-option>
        </mat-select>
      </mat-form-field>

      <mat-form-field appearance="outline">
        <mat-label>{{ 'year' | translate }}</mat-label>
        <mat-select formControlName="year" data-test="fms-diagnose-year">
          @for (year of years; track year) {
            <mat-option [value]="year">{{ year }}</mat-option>
          }
        </mat-select>
      </mat-form-field>

      <mat-form-field appearance="outline">
        <mat-label>{{ 'customer' | translate }}</mat-label>
        <input
          id="fms-diagnose-customer-search"
          matInput
          type="text"
          formControlName="customerName"
          [placeholder]="'customer' | translate"
          [attr.aria-label]="'customer' | translate"
          [matAutocomplete]="customerAutocomplete"
        />
        <mat-autocomplete #customerAutocomplete="matAutocomplete">
          @if (filteredCustomers$ | async; as customers) {
            @for (customer of customers; track customer.provider + customer.externalFarmId) {
              <mat-option [value]="providerCustomerName(customer)">
                {{ providerCustomerName(customer) }}
              </mat-option>
            }
          }
        </mat-autocomplete>
      </mat-form-field>
    </form>

    <ul class="search-possible-causes" data-test="fms-diagnose-search-possible-causes">
      <li data-test="fms-diagnose-search-possible-cause">
        {{ 'fmsDiagnose.tabs.linkage.search.customerMissing' | translate }}
      </li>
      <li data-test="fms-diagnose-search-possible-cause">
        {{ 'fmsDiagnose.tabs.linkage.search.kvkMismatch' | translate }}
      </li>
    </ul>

    @if (searchState$ | async; as searchState) {
      @if (searchState.kind === 'loading') {
        <output class="search-state" aria-live="polite">
          <mat-spinner
            diameter="32"
            [attr.aria-label]="'fmsDiagnose.tabs.linkage.search.loading' | translate"
          ></mat-spinner>
        </output>
      } @else if (searchState.kind === 'error') {
        <div class="search-state" role="alert">
          <span>{{ 'fmsDiagnose.tabs.linkage.search.error' | translate }}</span>
          <button mat-stroked-button type="button" (click)="requestSearchRetry()">
            {{ 'fmsDiagnose.tabs.linkage.search.retry' | translate }}
          </button>
        </div>
      } @else if (searchState.kind === 'empty') {
        <output class="search-state" aria-live="polite">
          {{ 'fmsDiagnose.tabs.linkage.search.noResults' | translate }}
        </output>
      } @else if (searchState.kind === 'loaded') {
        @if (filteredCustomers$ | async; as customers) {
          @if (customers.length === 0) {
            <output class="search-state" aria-live="polite">
              {{ 'fmsDiagnose.tabs.linkage.search.noResults' | translate }}
            </output>
          } @else {
            <div class="search-results">
              @for (customer of customers; track customer.provider + customer.externalFarmId) {
                <article class="search-result">
                  <div>
                    <h3>{{ providerCustomerName(customer) }}</h3>
                  </div>
                  <dl>
                    <dt>{{ 'fmsDiagnose.tabs.linkage.search.externalFarmId' | translate }}</dt>
                    <dd>{{ customer.externalFarmId }}</dd>
                    <dt>{{ 'fmsDiagnose.tabs.linkage.kvkInProvider' | translate }}</dt>
                    <dd>
                      @if (customer.kvkNumbers.length > 0) {
                        {{ customer.kvkNumbers.join(', ') }}
                      } @else {
                        {{ unavailable() }}
                      }
                    </dd>
                  </dl>
                </article>
              }
            </div>
          }
        }
      }
    }
  </section>
</main>
